/**
 * Helpers for the merged `date` field on transactions.
 *
 * Storage shape (v5+):
 *   - Date only:       'YYYY-MM-DD'
 *   - Date + time:     'YYYY-MM-DDTHH:MM'
 *
 * Lexicographic order matches chronological order in both shapes
 * (tested in `datetime.test.ts`), so existing date-string comparisons
 * keep working without parsing.
 *
 * Pre-v5 stored time in a separate `time` column. The migration
 * concatenates `${date}T${time}` for rows whose time was non-empty.
 */

/** Return the YYYY-MM-DD prefix regardless of whether time is appended. */
export function dayPart(stamp: string): string {
  if (!stamp) return '';
  const t = stamp.indexOf('T');
  return t === -1 ? stamp : stamp.slice(0, t);
}

/** Return the HH:MM suffix, or '' when none. */
export function timePart(stamp: string): string {
  if (!stamp) return '';
  const t = stamp.indexOf('T');
  return t === -1 ? '' : stamp.slice(t + 1, t + 6);
}

/** Combine local date + time into the merged datetime format. Empty
 *  `time` returns just the date — never produces a dangling `T`. */
export function joinDateTime(date: string, time: string | undefined | null): string {
  if (!date) return '';
  const t = (time ?? '').trim();
  if (!t) return date;
  // Accept HH:MM or HH:MM:SS — normalize to HH:MM for storage parity
  // with the AddTxScreen input control.
  const hm = t.length >= 5 ? t.slice(0, 5) : t;
  return `${date}T${hm}`;
}
